// components/auth/LoginForm.js - Complete with Email Verification Flow
import React, { useState, useRef, useEffect } from 'react';
import { Mail, Lock, LogIn, UserPlus, Eye, EyeOff, MessageCircle, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import GoogleOAuthButton from './GoogleOAuthButton';

const LoginForm = ({ onLoginSuccess, onNeedsVerification, api }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Enhanced validation states
  const [emailValidation, setEmailValidation] = useState({
    isValid: null,
    message: '',
    isChecking: false
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: '',
    color: '',
    checks: []
  });
  
  // Bot protection
  const [botProtection, setBotProtection] = useState({
    honeypot: '',
    timestamp: Date.now(),
    interactionCount: 0,
    mouseMovements: 0,
    keystrokes: 0
  });
  
  // Rate limiting info
  const [rateLimitInfo, setRateLimitInfo] = useState({
    attempts: 0,
    nextAttemptAt: null
  });

  const formRef = useRef(null);
  const emailTimeoutRef = useRef(null);

  // Track user interactions for bot detection
  const trackInteraction = (type = 'general') => {
    setBotProtection(prev => ({
      ...prev,
      interactionCount: prev.interactionCount + 1,
      keystrokes: type === 'keypress' ? prev.keystrokes + 1 : prev.keystrokes,
      mouseMovements: type === 'mouse' ? prev.mouseMovements + 1 : prev.mouseMovements
    }));
  };

  // Track mouse movements and keystrokes
  useEffect(() => {
    const handleMouseMove = () => trackInteraction('mouse');
    const handleKeyPress = () => trackInteraction('keypress');
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleKeyPress);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  // Real-time email validation
  const validateEmail = async (emailValue) => {
    if (!emailValue) {
      setEmailValidation({ isValid: null, message: '', isChecking: false });
      return;
    }

    // Basic format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      setEmailValidation({ 
        isValid: false, 
        message: 'Please enter a valid email address',
        isChecking: false
      });
      return;
    }

    // Check for disposable email domains
    const disposableDomains = [
      '10minutemail.com', 'tempmail.org', 'guerrillamail.com',
      'mailinator.com', 'throwaway.email', 'temp-mail.org',
      'temp-mail.com', '10mail.org', 'dispostable.com',
      'sharklasers.com', 'guerrillamailblock.com', 'pokemail.net',
      'spam4.me', 'bccto.me', 'maildrop.cc'
    ];
    
    const domain = emailValue.split('@')[1]?.toLowerCase();
    if (disposableDomains.includes(domain)) {
      setEmailValidation({ 
        isValid: false, 
        message: 'Disposable email addresses are not allowed',
        isChecking: false
      });
      return;
    }

    setEmailValidation(prev => ({ ...prev, isChecking: true }));

    try {
      // Server-side email validation
      const response = await fetch('/api/auth/validate-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue })
      });

      if (response.ok) {
        const result = await response.json();
        setEmailValidation({ 
          isValid: result.isValid, 
          message: result.message || (result.isValid ? 'Email looks good!' : 'Email validation failed'),
          isChecking: false
        });
      } else {
        // If validation service fails, accept format validation
        setEmailValidation({ 
          isValid: true, 
          message: 'Email format is valid',
          isChecking: false
        });
      }
    } catch (error) {
      // If validation service fails, accept format validation
      setEmailValidation({ 
        isValid: true, 
        message: 'Email format is valid',
        isChecking: false
      });
    }
  };

  // Password strength checker
  const checkPasswordStrength = (pwd) => {
    const checks = [
      { test: pwd.length >= 8, label: '8+ characters', weight: 1 },
      { test: /[A-Z]/.test(pwd), label: 'Uppercase', weight: 1 },
      { test: /[a-z]/.test(pwd), label: 'Lowercase', weight: 1 },
      { test: /\d/.test(pwd), label: 'Number', weight: 1 },
      { test: /[!@#$%^&*(),.?":{}|<>]/.test(pwd), label: 'Special char', weight: 1 }
    ];

    const passedChecks = checks.filter(check => check.test);
    const score = passedChecks.reduce((sum, check) => sum + check.weight, 0);
    
    let message = '';
    let color = '';
    
    if (score < 2) {
      message = 'Very weak';
      color = 'text-red-600';
    } else if (score < 3) {
      message = 'Weak';
      color = 'text-red-500';
    } else if (score < 4) {
      message = 'Fair';
      color = 'text-yellow-600';
    } else if (score < 5) {
      message = 'Good';
      color = 'text-green-500';
    } else {
      message = 'Strong';
      color = 'text-green-600';
    }

    return {
      score,
      message,
      color,
      checks: checks.map(check => ({ ...check, passed: check.test }))
    };
  };

  // Debounced email validation
  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    trackInteraction('keypress');
    
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }

    emailTimeoutRef.current = setTimeout(() => {
      validateEmail(emailValue);
    }, 500);
  };

  // Password change handler
  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    trackInteraction('keypress');
    
    if (!isLogin && pwd) {
      setPasswordStrength(checkPasswordStrength(pwd));
    }
  };

  // Bot protection validation
  const validateBotProtection = () => {
    // Honeypot check
    if (botProtection.honeypot) {
      return { isBot: true, reason: 'Honeypot triggered' };
    }

    // Time-based check (too fast submission)
    const timeSinceLoad = Date.now() - botProtection.timestamp;
    if (timeSinceLoad < 2000) {
      return { isBot: true, reason: 'Submission too fast' };
    }

    // Interaction checks
    if (botProtection.interactionCount < 3) {
      return { isBot: true, reason: 'Insufficient interactions' };
    }

    if (botProtection.keystrokes === 0 && (email || password || name)) {
      return { isBot: true, reason: 'No keyboard activity detected' };
    }

    return { isBot: false };
  };

  // Resend verification email (if needed for error handling)
  const handleResendVerification = async (emailToResend) => {
    try {
      await api.resendVerification(emailToResend);
      // Could show success message or handle in parent component
    } catch (err) {
      setError(err.message || 'Failed to resend verification email');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Check rate limiting
    if (rateLimitInfo.nextAttemptAt && Date.now() < rateLimitInfo.nextAttemptAt) {
      const remainingTime = Math.ceil((rateLimitInfo.nextAttemptAt - Date.now()) / 1000);
      setError(`Too many attempts. Please wait ${remainingTime} seconds.`);
      return;
    }

    // Email validation check
    if (emailValidation.isValid === false) {
      setError('Please enter a valid email address');
      return;
    }

    // Password strength check for registration
    if (!isLogin && passwordStrength.score < 3) {
      setError('Password is too weak. Please choose a stronger password.');
      return;
    }

    // Bot protection check
    const botCheck = validateBotProtection();
    if (botCheck.isBot) {
      setError('Security verification failed. Please try again.');
      console.warn('Bot protection triggered:', botCheck.reason);
      return;
    }

    setLoading(true);

    try {
      let response;
      
      // Prepare security data
      const securityData = {
        botProtection: {
          timestamp: botProtection.timestamp,
          interactionCount: botProtection.interactionCount,
          mouseMovements: botProtection.mouseMovements,
          keystrokes: botProtection.keystrokes,
          timeTaken: Date.now() - botProtection.timestamp,
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      };

      if (isLogin) {
        response = await api.login(email, password, securityData);
      } else {
        response = await api.register({
          email,
          password,
          name,
          ...securityData
        });
        
        // Handle registration success that requires verification
        if (response.requiresVerification) {
          if (onNeedsVerification) {
            onNeedsVerification(email);
          }
          setLoading(false);
          return; // Exit here - don't continue to token check
        }
      }

      // Handle login success OR registration without verification
      if (response.token) {
        api.setToken(response.token);
        onLoginSuccess(response.user);
      }

    } catch (err) {
      console.error('Auth error:', err);
      
      if (err.status === 429) {
        // Handle rate limiting
        const retryAfter = err.retryAfter || 60;
        setRateLimitInfo({
          attempts: err.attempts || 0,
          nextAttemptAt: Date.now() + (retryAfter * 1000)
        });
        setError(`Too many attempts. Please wait ${retryAfter} seconds.`);
      } else if (err.status === 403) {
        if (err.reason === 'bot_detected') {
          setError('Security verification failed. Please try again more carefully.');
          // Reset bot protection
          setBotProtection({
            honeypot: '',
            timestamp: Date.now(),
            interactionCount: 0,
            mouseMovements: 0,
            keystrokes: 0
          });
        } else if (err.message?.includes('verify your email')) {
          // Login blocked due to unverified email
          if (onNeedsVerification) {
            onNeedsVerification(email);
          }
          return;
        } else {
          setError(err.message || 'Access denied. Please try again.');
        }
      } else {
        setError(err.message || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle between login/register
  const handleToggle = (newIsLogin) => {
    setIsLogin(newIsLogin);
    setError('');
    setEmail('');
    setPassword('');
    setName('');
    setEmailValidation({ isValid: null, message: '', isChecking: false });
    setPasswordStrength({ score: 0, message: '', color: '', checks: [] });
    setBotProtection({
      honeypot: '',
      timestamp: Date.now(),
      interactionCount: 0,
      mouseMovements: 0,
      keystrokes: 0
    });
    trackInteraction();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl mb-4 shadow-lg">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">TicketDesk</h1>
          <p className="text-gray-600 mt-2">Customer Support Platform</p>
        </div>

        {/* Login/Register Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              type="button"
              onClick={() => handleToggle(true)}
              className={`flex-1 py-2 rounded-md font-medium transition-all ${
                isLogin 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => handleToggle(false)}
              className={`flex-1 py-2 rounded-md font-medium transition-all ${
                !isLogin 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Google OAuth Button */}
          <div className="mb-6">
            <GoogleOAuthButton 
              isLogin={isLogin} 
              loading={loading}
              disabled={loading}
            />
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>

          <form ref={formRef} onSubmit={handleSubmit}>
            {/* Honeypot field (hidden from users) */}
            <input
              type="text"
              name="website"
              value={botProtection.honeypot}
              onChange={(e) => setBotProtection(prev => ({ ...prev, honeypot: e.target.value }))}
              style={{ 
                position: 'absolute', 
                left: '-9999px', 
                opacity: 0,
                height: 0,
                width: 0
              }}
              tabIndex="-1"
              autoComplete="off"
            />

            {/* Name field (Register only) */}
            {!isLogin && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <UserPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      trackInteraction('keypress');
                    }}
                    onFocus={() => trackInteraction()}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    placeholder="John Doe"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            {/* Email field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onFocus={() => trackInteraction()}
                  onBlur={() => trackInteraction()}
                  className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                    emailValidation.isValid === false 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                      : emailValidation.isValid === true
                      ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                      : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500'
                  }`}
                  placeholder="you@example.com"
                  required
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {emailValidation.isChecking && (
                    <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full" />
                  )}
                  {!emailValidation.isChecking && emailValidation.isValid === true && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  {!emailValidation.isChecking && emailValidation.isValid === false && (
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </div>
              {emailValidation.message && (
                <p className={`mt-1 text-sm ${
                  emailValidation.isValid ? 'text-green-600' : 'text-red-600'
                }`}>
                  {emailValidation.message}
                </p>
              )}
            </div>

            {/* Password field */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onFocus={() => trackInteraction()}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowPassword(!showPassword);
                    trackInteraction();
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password strength indicator (Register only) */}
              {!isLogin && password && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          passwordStrength.score < 2 ? 'bg-red-500' :
                          passwordStrength.score < 3 ? 'bg-red-400' :
                          passwordStrength.score < 4 ? 'bg-yellow-500' :
                          passwordStrength.score < 5 ? 'bg-green-400' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min((passwordStrength.score / 5) * 100, 100)}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${passwordStrength.color}`}>
                      {passwordStrength.message}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {passwordStrength.checks.slice(0, 5).map((check, index) => (
                      <div key={index} className={`flex items-center ${
                        check.passed ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          check.passed ? 'bg-green-500' : 'bg-gray-300'
                        }`} />
                        {check.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm flex items-start">
                <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading || emailValidation.isValid === false || emailValidation.isChecking}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  {isLogin ? (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Sign In
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      Create Account
                    </>
                  )}
                </span>
              )}
            </button>

            {/* Forgot password link */}
            {isLogin && (
              <div className="text-center mt-4">
                <a href="#" className="text-sm text-purple-600 hover:text-purple-700">
                  Forgot your password?
                </a>
              </div>
            )}
          </form>
          
          {/* Security indicator */}
          <div className="mt-6 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center text-xs text-gray-600">
              <Shield className="h-3 w-3 mr-1" />
              <span>Protected by advanced bot detection & email verification</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {isLogin ? "Don't have an account?" : "Already have an account?"}
          <button
            type="button"
            onClick={() => handleToggle(!isLogin)}
            className="text-purple-600 hover:text-purple-700 font-medium ml-1"
          >
            {isLogin ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;